<template>
    <div class="p-grid crud-demo">
        <h1 class="p-mx-auto">Staff</h1>
        <div class="p-col-12">
            <div class="card">
                <Toast/>
                <Toolbar class="p-mb-4">
                    <template slot="left">
                        <Button label="New" icon="pi pi-plus" class="p-button-success p-m-1" @click="createStaff"/>
                        <Button label="Delete" icon="pi pi-trash" class="p-button-danger p-m-1"
                                @click="confirmDeleteSelectedStaffs"
                                :disabled="!selectedStaffs || !selectedStaffs.length"/>
                        <Button label="Export" icon="pi pi-upload" class="p-button-help p-m-1" @click="toggle"
                                :disabled="!selectedStaffs || !selectedStaffs.length || exporting"/>
                        <TieredMenu ref="menu" :model="items" :popup="true"/>
                    </template>
                </Toolbar>
                <ProgressBar mode="indeterminate" v-if="exporting"/>

                <DataTable ref="dt" :value="staffs" :selection.sync="selectedStaffs" dataKey="_jv.id"
                           :loading="loading"
                           :paginator="true"
                           class="p-datatable-customers"
                           :rows="10" :filters="filters"
                           paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                           :rowsPerPageOptions="[5,10,25]"
                           currentPageReportTemplate="Showing {first} to {last} of {totalRecords} staff"
                           :expandedRows.sync="expandedRows"
                           @row-expand="onRowExpand"
                >
                    <template #header>
                        <div class="table-header">
                            <h5 class="p-m-0">Manage Staff</h5>
                            <span class="p-input-icon-left">
                                <i class="pi pi-search"/>
                                <InputText v-model="filters['global']" placeholder="Search..."/>
                            </span>
                        </div>
                    </template>

                    <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>

                    <Column :expander="true" headerStyle="width: 3rem"></Column>
                    <Column field="name" header="Name" sortable filterMatchMode="contains">
                        <template #filter>
                            <InputText type="text" v-model="filters['name']" placeholder="Search by Name"
                                       style="width: 100%"/>
                        </template>
                    </Column>
                    <Column field="email" header="Email" sortable filterMatchMode="contains">
                        <template #filter>
                            <InputText type="text" v-model="filters['email']" placeholder="Search by Email"
                                       style="width: 100%"/>
                        </template>
                    </Column>
                    <Column field="job-title" header="Job Title" sortable filterMatchMode="contains">
                        <template #filter>
                            <InputText type="text" v-model="filters['job-title']" placeholder="Search by Job Title"
                                       style="width: 100%"/>
                        </template>
                    </Column>
                    <Column field="team.name" header="Team" sortable filterMatchMode="in">
                        <template #filter>
                            <MultiSelect v-model="filters['team.name']" :options="teams" optionLabel="name"
                                         optionValue="name" placeholder="All Teams" style="width: 100%">
                                <template #option="slotProps">
                                    <div>{{ slotProps.option.name }}</div>
                                </template>
                            </MultiSelect>
                        </template>
                    </Column>
                    <Column>
                        <template #body="slotProps">
                            <Button icon="pi pi-pencil" class="p-button-rounded p-button-success p-mr-2"
                                    @click="editStaff(slotProps.data)"/>
                            <Button icon="pi pi-trash" class="p-button-rounded p-button-warning"
                                    @click="confirmDeleteStaff(slotProps.data)"/>
                        </template>
                    </Column>

                    <template #expansion="slotProps">
                        <Accordion :multiple="true">
                            <AccordionTab :header="`Curriculum Vitae of ${slotProps.data.name}`" :active="true">
                                <div class="orders-subtable p-mx-2 p-my-2">
                                    <DocumentDataTable :documents="Object.values(slotProps.data.documents)"
                                                       :nested="true"
                                                       :showStaff="false"
                                                       :loading="loadingRel"/>
                                </div>
                            </AccordionTab>

                            <AccordionTab :header="`Academics of ${slotProps.data.name}`" :active="false">
                                <div class="orders-subtable p-mx-2 p-my-2">
                                    <AcademicDataTable :academics="Object.values(slotProps.data.academics)"
                                                       :nested="true"
                                                       :showStaff="false"
                                                       :loading="loadingRel"/>
                                </div>
                            </AccordionTab>

                            <AccordionTab :header="`Professionals of ${slotProps.data.name}`" :active="false">
                                <div class="orders-subtable p-mx-2 p-my-2">
                                    <ProfessionalDataTable :professionals="Object.values(slotProps.data.professionals)"
                                                           :nested="true"
                                                           :showStaff="false"
                                                           :loading="loadingRel"/>
                                </div>
                            </AccordionTab>

                            <AccordionTab :header="`Trainings of ${slotProps.data.name}`" :active="false">
                                <div class="orders-subtable p-mx-2 p-my-2">
                                    <TrainingDataTable :trainings="Object.values(slotProps.data.trainings)"
                                                       :nested="true"
                                                       :showStaff="false"
                                                       :loading="loadingRel"/>
                                </div>
                            </AccordionTab>
                        </Accordion>
                    </template>

                    <template #empty>
                        No records found.
                    </template>
                </DataTable>

                <StaffDialog ref="staffDialog" @updated="onItemsUpdated"/>
            </div>
        </div>
    </div>

</template>

<script>
import {Component, Vue} from 'vue-property-decorator'
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Toast from 'primevue/toast';
import Toolbar from 'primevue/toolbar';
import ProgressBar from 'primevue/progressbar';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import TieredMenu from 'primevue/tieredmenu';
import RadioButton from 'primevue/radiobutton';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import Calendar from 'primevue/calendar';
import StaffService from "@/service/StaffService";
import TeamService from "@/service/TeamService";
import store from "../store";
import StaffDialog from "../components/dialog/StaffDialog";
import AcademicDataTable from "../components/datatable/AcademicDataTable";
import ProfessionalDataTable from "../components/datatable/ProfessionalDataTable";
import TrainingDataTable from "../components/datatable/TrainingDataTable";
import ProfessionalService from "../service/ProfessionalService";
import TrainingService from "../service/TrainingService";
import DocumentDataTable from "../components/datatable/DocumentDataTable";

/**
 * Class-component
 */
@Component({
    components: {
        DocumentDataTable,
        TrainingDataTable,
        ProfessionalDataTable,
        AcademicDataTable,
        StaffDialog,
        Accordion: Vue.extend(Accordion),
        AccordionTab: Vue.extend(AccordionTab),
        Toast: Vue.extend(Toast),
        Toolbar: Vue.extend(Toolbar),
        ProgressBar: Vue.extend(ProgressBar),
        DataTable: Vue.extend(DataTable),
        Column: Vue.extend(Column),
        Dialog: Vue.extend(Dialog),
        Button: Vue.extend(Button),
        TieredMenu: Vue.extend(TieredMenu),
        RadioButton: Vue.extend(RadioButton),
        InputNumber: Vue.extend(InputNumber),
        InputText: Vue.extend(InputText),
        Message: Vue.extend(Message),
        MultiSelect: Vue.extend(MultiSelect),
        Calendar: Vue.extend(Calendar),
    }
})
export default class Staffs extends Vue {
    staffService = new StaffService();
    teamService = new TeamService();
    teams = [];
    staffs = [];
    staffDialog = false;
    selectedStaffs = [];
    expandedRows = [];
    professionals = null;
    trainings = null;
    filters = {};
    exporting = false;
    loading = false;
    loadingRel = null;
    items = [
        {
            label: 'Academics & Professionals', command: () => {
                this.exportCSV('academicsProfessionals')
            }
        },
        {
            label: 'Trainings', command: () => {
                this.exportCSV('trainings')
            }
        }
    ]
    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    async mounted() {
        try {
            this.loading = true;
            this.staffs = await this.staffService.getStaffs();
            this.teams = await this.teamService.getTeams()
            this.loading = false;
        } catch (e) {
            this.toastError.detail = e.message
            this.toastError.life = null
            this.$toast.add(this.toastError);
            this.staffService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                    life: null
                });
            })
        }
    }

    // expand rows
    async onRowExpand(event) {
        try {
            this.loadingRel = true
            await this.staffService.getStaffRelationships(event.data._jv.id, ['academics', 'professionals', 'professionals.professional', 'trainings', 'documents', 'team'], true)
            this.staffs = Object.values(await store.getters['jv/get']('staffs'))
            this.loadingRel = false
        } catch (e) {
            this.toastError.detail = e.message
            this.toastError.life = null
            this.$toast.add(this.toastError);
            this.staffService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                    life: null
                });
            })
        }
    }

    toggle(event) {
        this.$refs.menu.toggle(event);
    }

    async exportCSV(dataType) {
        try {
            this.exporting = true;
            if (this.selectedStaffs.length) {
                const professionalTitles = await new ProfessionalService().fetchAvailableTitles();
                const trainingCategories = new TrainingService().availableCategories();

                const papaObj = {
                    fields: [],
                    data: []
                };

                // 1. fields array
                if (dataType === 'academicsProfessionals') {
                    papaObj.fields = ['Staff Name', 'Academics'];
                    professionalTitles.forEach(title => {
                        papaObj.fields.push(title.name)
                    })
                } else if (dataType === 'trainings') {
                    papaObj.fields = ['Staff Name'];
                    trainingCategories.forEach(category => {
                        papaObj.fields.push(category.name)
                    })
                }

                // 2. data array
                for (const selectedStaff of this.selectedStaffs) {
                    await this.staffService.getStaffRelationships(selectedStaff._jv.id, ['academics', 'professionals', 'professionals.professional'], true)
                    const staff = await store.getters['jv/get'](`staffs/${selectedStaff._jv.id}`)

                    const staffArr = []
                    staffArr.push(staff.name)

                    if (dataType === 'academicsProfessionals') {
                        // academics
                        const academics = Object.values(staff.academics)
                        let academicString = ''
                        for (const academic of academics) {
                            academicString = academicString.concat(`${academic.title} `)
                        }
                        staffArr.push(academicString)

                        // professionals
                        const professionals = Object.values(staff.professionals)
                        professionalTitles.forEach(title => {
                            if (professionals.length) {
                                for (const professional of professionals) {
                                    if (title.name === professional.professional.title) {
                                        title.year = this.$moment(professional.date).year()
                                        break
                                    }
                                    title.year = '-'
                                }
                            } else {
                                title.year = '-'
                            }
                            staffArr.push(title.year)
                        })
                    } else if (dataType === 'trainings') {
                        // trainings (in the past year)
                        const date = this.$moment().subtract(1, 'year').startOf('month').toISOString();
                        const filteredTrainings = await this.staffService.filterStaffRelationship(selectedStaff._jv.id, 'trainings', 'date', 'gte', date)
                        const trainings = Object.values(filteredTrainings)
                        trainingCategories.forEach(category => {
                            let count = 0
                            for (const training of trainings) {
                                if (category.name === training.category) {
                                    count++
                                }
                            }
                            staffArr.push(count ? count : '-')
                        })
                    }

                    papaObj.data.push(staffArr)
                }

                this.csvString = this.$papaparse.unparse(papaObj)

                // 3. export csv
                if (dataType === 'academicsProfessionals') {
                    this.$exportCsv(this.csvString, `Academics-Professionals-Summary-${this.$moment().format('YYYY_MM_DD-HH_mm_ss')}`)
                } else if (dataType === 'trainings') {
                    this.$exportCsv(this.csvString, `Trainings-Summary-${this.$moment().format('YYYY_MM_DD-HH_mm_ss')}`)
                }

            } else {
                this.toastError.detail = 'Please select the staff below';
                this.toastError.life = 3000;
                this.$toast.add(this.toastError);
            }
            this.exporting = false;
        } catch (e) {
            this.toastError.detail = e.message
            this.toastError.life = null
            this.$toast.add(this.toastError);
            this.staffService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }
    }

    // listen to items updated
    async onItemsUpdated() {
        this.staffs = await this.staffService.getStaffs();
    }

    // Staff Dialog
    createStaff() {
        this.$refs.staffDialog.staff = {};
        this.$refs.staffDialog.staffDialog = true;
    }

    editStaff(staff) {
        this.$refs.staffDialog.staff = {...staff};
        this.$refs.staffDialog.staffDialog = true;
    }

    confirmDeleteStaff(staff) {
        this.$refs.staffDialog.staff = staff;
        this.$refs.staffDialog.deleteStaffDialog = true;
    }

    confirmDeleteSelectedStaffs() {
        this.$refs.staffDialog.selectedStaffs = this.selectedStaffs;
        this.$refs.staffDialog.deleteStaffsDialog = true;
    }
}

</script>

<style scoped lang="scss">
.table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.confirmation-content {
    display: flex;
    align-items: center;
    justify-content: center;
}

.p-tieredmenu {
    width: auto;
}

/* Responsive */
.p-datatable-customers .p-datatable-tbody > tr > td .p-column-title {
    display: none;
}

@media screen and (max-width: 960px) {
    /deep/ .p-datatable {
        &.p-datatable-customers {
            .p-datatable-thead > tr > th,
            .p-datatable-tfoot > tr > td {
                display: none !important;
            }

            .p-datatable-tbody > tr {
                border-bottom: 1px solid var(--layer-2);

                > td {
                    text-align: left;
                    display: block;
                    border: 0 none !important;
                    width: 100% !important;
                    float: left;
                    clear: left;
                    border: 0 none;

                    .p-column-title {
                        padding: .4rem;
                        min-width: 30%;
                        display: inline-block;
                        margin: -.4rem 1rem -.4rem -.4rem;
                        font-weight: bold;
                    }

                    .p-progressbar {
                        margin-top: .5rem;
                    }
                }
            }
        }
    }
}
</style>
